import React, { TableHTMLAttributes } from 'react';

export interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  striped?: boolean;
  bordered?: boolean;
  hoverable?: boolean;
  compact?: boolean;
}

export const Table: React.FC<TableProps> = ({
  children,
  striped = false,
  bordered = false,
  hoverable = false,
  compact = false,
  className = '',
  ...props
}) => {
  // Base classes
  const baseClasses = 'min-w-full divide-y divide-gray-200';
  
  // Combine all classes
  const tableClasses = `${baseClasses} ${className}`;
  
  return (
    <div className="overflow-x-auto">
      <table className={tableClasses} {...props}>
        {children}
      </table>
    </div>
  );
};

export interface TableHeadProps extends React.HTMLAttributes<HTMLTableSectionElement> {}

export const TableHead: React.FC<TableHeadProps> = ({
  children,
  className = '',
  ...props
}) => {
  const baseClasses = 'bg-gray-50';
  const headClasses = `${baseClasses} ${className}`;
  
  return (
    <thead className={headClasses} {...props}>
      {children}
    </thead>
  );
};

export interface TableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  striped?: boolean;
  hoverable?: boolean;
}

export const TableBody: React.FC<TableBodyProps> = ({
  children,
  striped = false,
  hoverable = false,
  className = '',
  ...props
}) => {
  const baseClasses = 'bg-white divide-y divide-gray-200';
  const stripedClasses = striped ? 'divide-y-0' : '';
  const bodyClasses = `${baseClasses} ${stripedClasses} ${className}`;
  
  return (
    <tbody className={bodyClasses} {...props}>
      {children}
    </tbody>
  );
};

export interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  striped?: boolean;
  hoverable?: boolean;
  isEven?: boolean;
}

export const TableRow: React.FC<TableRowProps> = ({
  children,
  striped = false,
  hoverable = false,
  isEven = false,
  className = '',
  ...props
}) => {
  const baseClasses = '';
  const stripedClasses = striped && isEven ? 'bg-gray-50' : '';
  const hoverableClasses = hoverable ? 'hover:bg-gray-100' : '';
  const rowClasses = `${baseClasses} ${stripedClasses} ${hoverableClasses} ${className}`;
  
  return (
    <tr className={rowClasses} {...props}>
      {children}
    </tr>
  );
};

export interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  compact?: boolean;
  bordered?: boolean;
}

export const TableCell: React.FC<TableCellProps> = ({
  children,
  compact = false,
  bordered = false,
  className = '',
  ...props
}) => {
  const baseClasses = 'text-sm text-gray-900';
  const paddingClasses = compact ? 'px-3 py-2' : 'px-6 py-4';
  const borderClasses = bordered ? 'border border-gray-200' : '';
  const cellClasses = `${baseClasses} ${paddingClasses} ${borderClasses} ${className}`;
  
  return (
    <td className={cellClasses} {...props}>
      {children}
    </td>
  );
};

export interface TableHeaderCellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  compact?: boolean;
  bordered?: boolean;
  sortable?: boolean;
  sorted?: 'asc' | 'desc' | null;
  onSort?: () => void;
}

export const TableHeaderCell: React.FC<TableHeaderCellProps> = ({
  children,
  compact = false,
  bordered = false,
  sortable = false,
  sorted = null,
  onSort,
  className = '',
  ...props
}) => {
  const baseClasses = 'text-xs font-medium text-gray-500 uppercase tracking-wider text-left';
  const paddingClasses = compact ? 'px-3 py-2' : 'px-6 py-3';
  const borderClasses = bordered ? 'border border-gray-200' : '';
  const sortableClasses = sortable ? 'cursor-pointer' : '';
  const headerClasses = `${baseClasses} ${paddingClasses} ${borderClasses} ${sortableClasses} ${className}`;
  
  const handleClick = () => {
    if (sortable && onSort) {
      onSort();
    }
  };
  
  return (
    <th className={headerClasses} onClick={handleClick} {...props}>
      <div className="flex items-center">
        {children}
        {sortable && (
          <span className="ml-1">
            {sorted === 'asc' && (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            )}
            {sorted === 'desc' && (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
            {sorted === null && (
              <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
            )}
          </span>
        )}
      </div>
    </th>
  );
};

export default Object.assign(Table, {
  Head: TableHead,
  Body: TableBody,
  Row: TableRow,
  Cell: TableCell,
  HeaderCell: TableHeaderCell,
});
